import React, { useEffect, useMemo, useState, useCallback } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  startOfWeek, addWeeks, addDays, format, parseISO, startOfDay,
} from "date-fns";
import { it } from "date-fns/locale";
import {
  CalendarClock, ChevronLeft, ChevronRight, Users, ArrowLeftRight, Check, X,
  Clock, Ban, Copy, BarChart3, Download,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { SHIFT_TYPES, SHIFT_LABELS, SHIFT_SHORT, SHIFT_COLORS } from "../shifts";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const mondayOf = (d) => startOfWeek(d, { weekStartsOn: 1 });
const iso = (d) => format(d, "yyyy-MM-dd");
const fmtDay = (isoStr) => { try { return format(parseISO(isoStr), "EEE dd MMM", { locale: it }); } catch { return isoStr; } };

// Durata in ore di una fascia (gestisce il turno notte che attraversa la mezzanotte).
const shiftDurationH = (hours, type) => {
  const r = hours?.[type];
  if (!r?.start || !r?.end) return 0;
  const [sh, sm] = r.start.split(":").map(Number);
  const [eh, em] = r.end.split(":").map(Number);
  let d = (eh * 60 + em) - (sh * 60 + sm);
  if (d <= 0) d += 1440;
  return d / 60;
};

const SWAP_STATUS = {
  in_attesa_collega: { label: "In attesa del collega", cls: "bg-amber-100 text-amber-800" },
  in_attesa_responsabile: { label: "In attesa del responsabile", cls: "bg-blue-100 text-blue-800" },
  approvata: { label: "Approvato", cls: "bg-green-100 text-green-700" },
  rifiutata: { label: "Rifiutato", cls: "bg-red-100 text-red-700" },
  annullata: { label: "Annullato", cls: "bg-gray-100 text-gray-500" },
};

/* -------------------- Griglia settimanale (responsabile) -------------------- */
const WeekGrid = ({ hours }) => {
  const [depts, setDepts] = useState([]);
  const [deptId, setDeptId] = useState("");
  const [cursor, setCursor] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shifts/managed-departments").then((r) => {
      setDepts(r.data || []);
      if (r.data?.length) setDeptId((d) => d || r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!deptId) return;
    api.get(`/shifts/week?start=${iso(cursor)}&department_id=${deptId}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [deptId, cursor]);
  useEffect(() => { load(); }, [load]);

  const shiftMap = useMemo(() => {
    const m = {};
    (data?.shifts || []).forEach((s) => { m[`${s.employee_id}|${s.date}|${s.type}`] = s.id; });
    return m;
  }, [data]);

  const summary = useMemo(() => {
    const byEmp = {};
    (data?.members || []).forEach((m) => { byEmp[m.id] = { count: 0, hours: 0, mattina: 0, pomeriggio: 0, notte: 0 }; });
    (data?.shifts || []).forEach((s) => {
      const e = byEmp[s.employee_id]; if (!e) return;
      e.count += 1; e[s.type] += 1; e.hours += shiftDurationH(hours, s.type);
    });
    return byEmp;
  }, [data, hours]);

  const coverage = useMemo(() => {
    const cov = {};
    (data?.days || []).forEach((d) => { cov[d] = { mattina: 0, pomeriggio: 0, notte: 0 }; });
    (data?.shifts || []).forEach((s) => { if (cov[s.date]) cov[s.date][s.type] += 1; });
    return cov;
  }, [data]);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyWeeks, setCopyWeeks] = useState("1");
  const doCopy = async () => {
    setBusy(true);
    try {
      const r = await api.post("/shifts/copy-week", { department_id: deptId, source_week_start: iso(cursor), weeks: Number(copyWeeks) });
      toast.success(`${r.data?.created || 0} turni copiati sulle ${copyWeeks} settimane successive`);
      setCopyOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const toggle = async (empId, day, type) => {
    if (busy) return;
    const key = `${empId}|${day}|${type}`;
    const existingId = shiftMap[key];
    setBusy(true);
    try {
      if (existingId) {
        await api.delete(`/shifts/${existingId}`);
      } else {
        await api.post("/shifts", { employee_id: empId, date: day, type });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const days = data?.days || [];

  return (
    <section className="mb-8" data-testid="shift-week-grid">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-[16px] font-bold text-[#16345c] flex items-center gap-2"><Users size={18} /> Pianificazione turni del reparto</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {depts.length > 1 && (
            <div className="w-[200px]">
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger data-testid="shift-dept-select"><SelectValue placeholder="Reparto" /></SelectTrigger>
                <SelectContent>
                  {depts.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`shift-dept-${d.id}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <button onClick={() => setCursor(addWeeks(cursor, -1))} data-testid="shift-week-prev" className="w-9 h-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50 text-[#16345c]"><ChevronLeft size={18} /></button>
          <span className="text-[13px] font-bold text-[#16345c] min-w-[190px] text-center">
            {days.length ? `${fmtDay(days[0])} → ${fmtDay(days[6])}` : "—"}
          </span>
          <button onClick={() => setCursor(addWeeks(cursor, 1))} data-testid="shift-week-next" className="w-9 h-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50 text-[#16345c]"><ChevronRight size={18} /></button>
          <button onClick={() => setCursor(mondayOf(new Date()))} data-testid="shift-week-today" className="px-3 h-9 text-[13px] font-semibold rounded-sm border border-gray-300 bg-white hover:bg-gray-50 text-[#16345c]">Questa settimana</button>
          <button onClick={() => setCopyOpen(true)} disabled={!deptId} data-testid="shift-copy-week-open" className="flex items-center gap-1.5 px-3 h-9 text-[13px] font-bold rounded-sm bg-[#7c3aed] hover:bg-[#6d28d9] text-white transition-colors disabled:opacity-60"><Copy size={15} /> Copia settimana</button>
        </div>
      </div>

      {!deptId ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Non gestisci alcun reparto.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full border-collapse min-w-[760px]" data-testid="shift-grid-table">
            <thead>
              <tr className="bg-[#16345c] text-white">
                <th className="text-left text-[12px] font-bold uppercase tracking-wide px-3 py-2.5 sticky left-0 bg-[#16345c] z-10 min-w-[160px]">Dipendente</th>
                {days.map((d, i) => (
                  <th key={d} className="text-center text-[11px] font-bold uppercase px-1 py-2.5 min-w-[84px]">
                    <div>{WD[i]}</div>
                    <div className="text-[10px] font-semibold opacity-80">{format(parseISO(d), "dd/MM")}</div>
                  </th>
                ))}
                <th className="text-center text-[11px] font-bold uppercase px-2 py-2.5 min-w-[92px] bg-[#0d1b2f]">Tot sett.</th>
              </tr>
            </thead>
            <tbody>
              {(data?.members || []).length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-[13px]">Nessun dipendente nel reparto</td></tr>
              ) : (data?.members || []).map((m) => (
                <tr key={m.id} className="border-b border-gray-100 last:border-0" data-testid={`shift-row-${m.id}`}>
                  <td className="text-[13px] font-semibold text-[#16345c] px-3 py-2 sticky left-0 bg-white z-10">{m.name}</td>
                  {days.map((d) => (
                    <td key={d} className="px-1 py-1.5 text-center align-top">
                      <div className="flex items-center justify-center gap-1">
                        {SHIFT_TYPES.map((t) => {
                          const active = !!shiftMap[`${m.id}|${d}|${t}`];
                          return (
                            <button
                              key={t}
                              onClick={() => toggle(m.id, d, t)}
                              disabled={busy}
                              data-testid={`shift-cell-${m.id}-${d}-${t}`}
                              title={`${SHIFT_LABELS[t]} ${hours?.[t]?.start || ""}-${hours?.[t]?.end || ""}`}
                              className="w-6 h-6 rounded-md text-[11px] font-black flex items-center justify-center transition-colors disabled:opacity-50"
                              style={active
                                ? { backgroundColor: SHIFT_COLORS[t], color: "#fff" }
                                : { backgroundColor: `${SHIFT_COLORS[t]}1a`, color: SHIFT_COLORS[t] }}
                            >
                              {SHIFT_SHORT[t]}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center bg-gray-50" data-testid={`shift-summary-${m.id}`}>
                    <div className="text-[13px] font-black text-[#16345c]">{summary[m.id]?.count || 0}t · {Math.round((summary[m.id]?.hours || 0) * 10) / 10}h</div>
                    <div className="text-[10px] text-gray-500 font-semibold flex items-center justify-center gap-1">
                      {SHIFT_TYPES.map((t) => (summary[m.id]?.[t] ? <span key={t} style={{ color: SHIFT_COLORS[t] }}>{summary[m.id][t]}{SHIFT_SHORT[t]}</span> : null))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {(data?.members || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50" data-testid="shift-coverage-row">
                  <td className="text-[11px] font-bold uppercase text-gray-500 px-3 py-2 sticky left-0 bg-gray-50 z-10">Copertura</td>
                  {days.map((d) => (
                    <td key={d} className="px-1 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {SHIFT_TYPES.map((t) => {
                          const n = coverage[d]?.[t] || 0;
                          const uncovered = n === 0;
                          return (
                            <span key={t} data-testid={`coverage-${d}-${t}`} title={uncovered ? `${SHIFT_LABELS[t]}: scoperto` : `${SHIFT_LABELS[t]}: ${n}`}
                              className="w-6 h-5 rounded text-[10px] font-black flex items-center justify-center border"
                              style={uncovered
                                ? { backgroundColor: "#fee2e2", color: "#b91c1c", borderColor: "#fecaca" }
                                : { backgroundColor: `${SHIFT_COLORS[t]}1a`, color: SHIFT_COLORS[t], borderColor: `${SHIFT_COLORS[t]}55` }}>
                              {uncovered ? "0" : n}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  ))}
                  <td className="bg-gray-50" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      <p className="text-[12px] text-gray-500 mt-2">Clicca su <b>M</b> / <b>P</b> / <b>N</b> per assegnare o rimuovere il turno. Sono ammessi più turni nello stesso giorno. Nella riga <b>Copertura</b> le fasce <span className="text-red-600 font-bold">rosse</span> sono scoperte (nessun dipendente assegnato).</p>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Copia settimana (turni ricorrenti)</DialogTitle></DialogHeader>
          <p className="text-[14px] text-gray-600">Copia tutti i turni della settimana <b>{days.length ? `${fmtDay(days[0])} → ${fmtDay(days[6])}` : ""}</b> sulle settimane successive. I turni già presenti non vengono duplicati.</p>
          <div>
            <label className="text-[12px] font-bold text-gray-700 block mb-1">Numero di settimane successive</label>
            <Select value={copyWeeks} onValueChange={setCopyWeeks}>
              <SelectTrigger data-testid="copy-weeks-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["1", "2", "3", "4"].map((n) => <SelectItem key={n} value={n} data-testid={`copy-weeks-${n}`}>{n} settiman{n === "1" ? "a" : "e"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <button onClick={() => setCopyOpen(false)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={doCopy} disabled={busy} data-testid="copy-week-confirm" className="px-4 py-2 text-[13px] font-bold text-white bg-[#7c3aed] hover:bg-[#6d28d9] rounded-sm transition-colors disabled:opacity-60">Copia</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

/* -------------------- I miei turni + richiesta scambio (dipendente) -------------------- */
const MyShifts = ({ hours, onSwapCreated }) => {
  const [shifts, setShifts] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [swapFor, setSwapFor] = useState(null);
  const [colleagueId, setColleagueId] = useState("");
  const [colShifts, setColShifts] = useState([]);
  const [reciprocalId, setReciprocalId] = useState("none");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const start = iso(startOfDay(new Date()));
    const end = iso(addDays(new Date(), 60));
    api.get(`/shifts/mine?start=${start}&end=${end}`).then((r) => setShifts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/shifts/colleagues").then((r) => setColleagues(r.data || [])).catch(() => {}); }, []);

  // Turni del collega selezionato (per lo scambio reciproco).
  useEffect(() => {
    setReciprocalId("none");
    setColShifts([]);
    if (!colleagueId) return;
    const start = iso(startOfDay(new Date()));
    const end = iso(addDays(new Date(), 60));
    api.get(`/shifts/colleague-shifts?colleague_id=${colleagueId}&start=${start}&end=${end}`)
      .then((r) => setColShifts(r.data || [])).catch(() => setColShifts([]));
  }, [colleagueId]);

  const openSwap = (s) => { setSwapFor(s); setColleagueId(""); setReciprocalId("none"); setColShifts([]); setNote(""); };

  const submitSwap = async () => {
    if (!colleagueId) { toast.error("Seleziona un collega"); return; }
    setBusy(true);
    try {
      await api.post("/shifts/swaps", {
        shift_id: swapFor.id,
        colleague_id: colleagueId,
        colleague_shift_id: reciprocalId !== "none" ? reciprocalId : null,
        note,
      });
      toast.success("Proposta di scambio inviata al collega");
      setSwapFor(null); onSwapCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <section className="mb-8" data-testid="my-shifts">
      <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Clock size={18} /> I miei turni</h2>
      {shifts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun turno assegnato nei prossimi giorni.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`my-shift-${s.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2.5 h-8 rounded-sm shrink-0" style={{ backgroundColor: SHIFT_COLORS[s.type] }} />
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-[#16345c] capitalize">{fmtDay(s.date)}</div>
                  <div className="text-[12px] text-gray-500">{SHIFT_LABELS[s.type]} · {s.start}–{s.end}</div>
                </div>
              </div>
              <button onClick={() => openSwap(s)} data-testid={`swap-request-${s.id}`} className="flex items-center gap-1.5 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors shrink-0">
                <ArrowLeftRight size={14} /> Scambia
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!swapFor} onOpenChange={(o) => !o && setSwapFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Richiedi scambio turno</DialogTitle></DialogHeader>
          {swapFor && (
            <p className="text-[14px] text-gray-600">
              Turno: <b>{SHIFT_LABELS[swapFor.type]}</b> del <b className="capitalize">{fmtDay(swapFor.date)}</b> ({swapFor.start}–{swapFor.end})
            </p>
          )}
          <div>
            <label className="text-[12px] font-bold text-gray-700 block mb-1">Collega (stesso reparto)</label>
            <Select value={colleagueId} onValueChange={setColleagueId}>
              <SelectTrigger data-testid="swap-colleague-select"><SelectValue placeholder="Seleziona un collega" /></SelectTrigger>
              <SelectContent>
                {colleagues.length === 0 ? (
                  <div className="px-3 py-2 text-[13px] text-gray-400">Nessun collega disponibile</div>
                ) : colleagues.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`swap-colleague-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[12px] font-bold text-gray-700 block mb-1">In cambio ricevi (facoltativo)</label>
            <Select value={reciprocalId} onValueChange={setReciprocalId} disabled={!colleagueId}>
              <SelectTrigger data-testid="swap-reciprocal-select"><SelectValue placeholder="Nessuno — cedo solo il mio turno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="swap-reciprocal-none">Nessuno — cedo solo il mio turno</SelectItem>
                {colShifts.map((cs) => (
                  <SelectItem key={cs.id} value={cs.id} data-testid={`swap-reciprocal-${cs.id}`}>
                    {SHIFT_LABELS[cs.type]} · {fmtDay(cs.date)} ({cs.start}–{cs.end})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-1">
              {!colleagueId ? "Seleziona prima un collega."
                : colShifts.length === 0 ? "Il collega non ha turni programmati da scambiare."
                : "Scegli un turno del collega da prendere tu in cambio (scambio reciproco)."}
            </p>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Motivo o nota (facoltativa)…" data-testid="swap-note"
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
          <p className="text-[12px] text-gray-500">Il collega dovrà prima accettare, poi il responsabile approverà lo scambio.</p>
          <DialogFooter>
            <button onClick={() => setSwapFor(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={submitSwap} disabled={busy} data-testid="swap-submit" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-60">Invia proposta</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

/* -------------------- Scambi turno (richieste in ingresso e mie) -------------------- */
const MySwaps = ({ reloadKey }) => {
  const [swaps, setSwaps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/shifts/swaps/mine").then((r) => setSwaps(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const colleagueDecision = async (id, decision) => {
    setBusy(true);
    try {
      await api.post(`/shifts/swaps/${id}/colleague-decision`, { decision });
      toast.success(decision === "accept" ? "Proposta accettata, in attesa del responsabile" : "Proposta rifiutata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    setBusy(true);
    try { await api.post(`/shifts/swaps/${id}/cancel`); toast.success("Richiesta annullata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const incoming = swaps.filter((s) => s.am_colleague && s.status === "in_attesa_collega");
  const others = swaps.filter((s) => !(s.am_colleague && s.status === "in_attesa_collega"));

  return (
    <section data-testid="my-swaps">
      <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><ArrowLeftRight size={18} /> Scambi turno</h2>

      {incoming.length > 0 && (
        <div className="mb-5">
          <div className="text-[12px] font-bold uppercase tracking-wide text-amber-700 mb-2">Da confermare (proposte ricevute)</div>
          <div className="space-y-2">
            {incoming.map((s) => (
              <div key={s.id} className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid={`swap-incoming-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] text-[#16345c]"><b>{s.requester_name}</b> ti propone di prendere il suo turno</div>
                  <div className="text-[12px] text-gray-600">{s.shift_type_label} · <span className="capitalize">{fmtDay(s.shift_date)}</span>{s.note ? ` · "${s.note}"` : ""}</div>
                  {s.reciprocal && s.colleague_shift_date && (
                    <div className="text-[12px] text-[#7c3aed] font-semibold" data-testid={`swap-incoming-reciprocal-${s.id}`}>In cambio cederesti il tuo turno: {s.colleague_shift_type_label} · <span className="capitalize">{fmtDay(s.colleague_shift_date)}</span></div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => colleagueDecision(s.id, "accept")} disabled={busy} data-testid={`swap-accept-${s.id}`} className="flex items-center gap-1 bg-[#009640] hover:bg-[#007a34] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"><Check size={14} /> Accetta</button>
                  <button onClick={() => colleagueDecision(s.id, "decline")} disabled={busy} data-testid={`swap-decline-${s.id}`} className="flex items-center gap-1 bg-white border border-red-300 text-[#c00510] hover:bg-red-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"><X size={14} /> Rifiuta</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[12px] font-bold uppercase tracking-wide text-gray-500 mb-2">Storico e stato</div>
      {others.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-[13px]">Nessuno scambio turno.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {others.map((s) => {
            const st = SWAP_STATUS[s.status] || { label: s.status, cls: "bg-gray-100 text-gray-600" };
            return (
              <div key={s.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`swap-item-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] text-[#16345c]">
                    {s.am_requester ? <>Turno ceduto a <b>{s.colleague_name}</b></> : <>Turno da <b>{s.requester_name}</b></>}
                  </div>
                  <div className="text-[12px] text-gray-600">{s.shift_type_label} · <span className="capitalize">{fmtDay(s.shift_date)}</span></div>
                  {s.reciprocal && s.colleague_shift_date && (
                    <div className="text-[12px] text-[#7c3aed] font-semibold" data-testid={`swap-reciprocal-info-${s.id}`}>
                      {s.am_requester
                        ? <>In cambio ricevi: {s.colleague_shift_type_label} · <span className="capitalize">{fmtDay(s.colleague_shift_date)}</span></>
                        : <>In cambio cedi: {s.colleague_shift_type_label} · <span className="capitalize">{fmtDay(s.colleague_shift_date)}</span></>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`swap-status-${s.id}`}>{st.label}</span>
                  {s.am_requester && (s.status === "in_attesa_collega" || s.status === "in_attesa_responsabile") && (
                    <button onClick={() => cancel(s.id)} disabled={busy} data-testid={`swap-cancel-${s.id}`} className="flex items-center gap-1 text-gray-500 hover:text-[#c00510] text-[12px] font-semibold"><Ban size={13} /> Annulla</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/* -------------------- Statistiche mensili (responsabile) -------------------- */
const monthStr = (d) => format(d, "yyyy-MM");
const ShiftStats = () => {
  const [depts, setDepts] = useState([]);
  const [deptId, setDeptId] = useState("");
  const [month, setMonth] = useState(monthStr(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shifts/managed-departments").then((r) => {
      setDepts(r.data || []);
      if (r.data?.length) setDeptId((d) => d || r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!deptId || !month) return;
    api.get(`/shifts/stats?month=${month}&department_id=${deptId}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [deptId, month]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    if (!deptId || !month) return;
    setBusy(true);
    try {
      const r = await api.get(`/shifts/stats/export?month=${month}&department_id=${deptId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `turni_${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione non riuscita"); }
    finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  const tot = data?.totals;

  return (
    <section className="mb-8" data-testid="shift-stats">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-[16px] font-bold text-[#16345c] flex items-center gap-2"><BarChart3 size={18} /> Statistiche mensili turni</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {depts.length > 1 && (
            <div className="w-[200px]">
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger data-testid="stats-dept-select"><SelectValue placeholder="Reparto" /></SelectTrigger>
                <SelectContent>
                  {depts.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`stats-dept-${d.id}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="stats-month"
            className="border border-gray-300 rounded-sm px-3 h-9 text-[13px] text-[#16345c] outline-none focus:border-[#16345c]" />
          <button onClick={exportCsv} disabled={busy || !rows.length} data-testid="stats-export-csv"
            className="flex items-center gap-1.5 px-3 h-9 text-[13px] font-bold rounded-sm bg-[#009640] hover:bg-[#007a34] text-white transition-colors disabled:opacity-50">
            <Download size={15} /> Esporta CSV
          </button>
        </div>
      </div>

      {!deptId ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Non gestisci alcun reparto.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full border-collapse min-w-[640px]" data-testid="stats-table">
            <thead>
              <tr className="bg-[#16345c] text-white">
                <th className="text-left text-[12px] font-bold uppercase tracking-wide px-3 py-2.5 min-w-[180px]">Dipendente</th>
                {SHIFT_TYPES.map((t) => <th key={t} className="text-center text-[11px] font-bold uppercase px-2 py-2.5">{SHIFT_LABELS[t]}</th>)}
                <th className="text-center text-[11px] font-bold uppercase px-2 py-2.5">Turni</th>
                <th className="text-center text-[11px] font-bold uppercase px-2 py-2.5 bg-[#0d1b2f]">Ore</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-[13px]">Nessun turno nel mese selezionato.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.employee_id} className="border-b border-gray-100 last:border-0" data-testid={`stats-row-${r.employee_id}`}>
                  <td className="text-[13px] font-semibold text-[#16345c] px-3 py-2">{r.name}</td>
                  {SHIFT_TYPES.map((t) => <td key={t} className="text-center text-[13px] font-bold px-2 py-2" style={{ color: r[t] ? SHIFT_COLORS[t] : "#cbd5e1" }}>{r[t] || 0}</td>)}
                  <td className="text-center text-[13px] font-bold px-2 py-2 text-[#16345c]">{r.total_shifts}</td>
                  <td className="text-center text-[13px] font-black px-2 py-2 bg-gray-50 text-[#16345c]">{r.total_hours}h</td>
                </tr>
              ))}
            </tbody>
            {tot && rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50" data-testid="stats-totals">
                  <td className="text-[12px] font-bold uppercase text-gray-500 px-3 py-2">Totale reparto</td>
                  {SHIFT_TYPES.map((t) => <td key={t} className="text-center text-[13px] font-bold px-2 py-2 text-[#16345c]">{tot[t] || 0}</td>)}
                  <td className="text-center text-[13px] font-bold px-2 py-2 text-[#16345c]">{tot.total_shifts}</td>
                  <td className="text-center text-[13px] font-black px-2 py-2 text-[#16345c]">{tot.total_hours}h</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
};

const ShiftsPage = () => {
  const { isApprover } = useAuth();
  const [hours, setHours] = useState({});
  const [swapReload, setSwapReload] = useState(0);

  useEffect(() => {
    api.get("/shifts/config").then((r) => setHours(r.data?.hours || {})).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><CalendarClock size={24} /> Turni</h1>
          <p className="text-[14px] text-gray-500">Pianifica i turni del reparto, consulta i tuoi e richiedi scambi con i colleghi.</p>
        </div>

        {/* Legenda */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          {SHIFT_TYPES.map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[12px] text-gray-600">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SHIFT_COLORS[t] }} />
              {SHIFT_LABELS[t]} {hours?.[t] ? `(${hours[t].start}–${hours[t].end})` : ""}
            </span>
          ))}
        </div>

        {isApprover && <WeekGrid hours={hours} />}
        {isApprover && <ShiftStats />}
        <MyShifts hours={hours} onSwapCreated={() => setSwapReload((k) => k + 1)} />
        <MySwaps reloadKey={swapReload} />
      </main>
    </div>
  );
};

export default ShiftsPage;
